/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppSelector } from "@redux/hooks";

// 1) Importamos tu GenericTable “expandible”
import { GenericTable } from "@ui-components/Table/GenericTable";
// 2) Columnas base: prefix, family, etc.
import { BgpTables, tablesColumns } from "./BgpTablesData";

// 3) Servicios
import {
  getCTXRlistGlobal,
  getNeighborsListByCtxrId,
  postBgpSummary,
} from "./request/request";

import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import {
  buildPredicate,
  FilterField,
  labelId,
  parseToken,
  ParsedFilter,
} from "kontrolEdge/helpers/advancedFilters";
import { makeBgpFields } from "./bgpFieldSpec";

import { ReactComponent as IconStar } from "@assets/Icons/Icon_Eye.svg";
import { ReactComponent as IconClosed } from "@assets/Icons/Icon_EyeSlash.svg";
import axios from "axios";
import GenericCard from "@ui-components/Card/GenericCard";
import { SkeletonTableRows } from "@ui-components/Skeleton/GenericSkeleton";

interface CtxrItem {
  id: string;
  name: string;
  description: string;
  subscriptionId: number;
  Suscription?: string;
}

/** One row of `POST /v1/ctxr/bgpsumary/:id` (chopper-backend `CTXRController.bgpsumary`). */
interface BgpSummaryItem {
  nlri?: string;
  family?: string;
  neighbor?: string;
  localpreference?: string;
  community?: string;
  extendedcommunity?: string;
  type?: string;
  routerid?: string;
  aspath?: string;
  localip?: string;
  localas?: string;
  peeras?: string;
  subnet?: string;
  route_table?: string;
  nexthop?: string | null;
  origin?: string | null;
  rd?: string | null;
  label?: string | null;
  familyallowed?: string | null;
}
/** Extendemos BgpTables para la subfila expandida */
export interface ExtendedBgpTables extends BgpTables {
  routerId?: string;
  asPath?: string;
  localIp?: string;
  localAs?: string;
  peerAs?: string;
  subnet?: string;
  route_table?: string;
  nextHop?: string;
  origin?: string;
  rd?: string;
  label?: string;
  familyAllowed?: string;
}

export function mapBgpSummaryToRows(data: BgpSummaryItem[]): ExtendedBgpTables[] {
  return data.map((item) => ({
    prefix: item.nlri || "",
    family: item.family || "",
    neighbor: item.neighbor || "",
    localPref: item.localpreference || "",
    community: item.community || "",
    extendedCommunity: item.extendedcommunity || "",
    direction: item.type || "",
    routerId: item.routerid || "",
    asPath: item.aspath || "",
    localIp: item.localip || "",
    localAs: item.localas || "",
    peerAs: item.peeras || "",
    subnet: item.subnet || "",
    route_table: item.route_table || "",
    nextHop: item.nexthop || "",
    origin: item.origin || "",
    rd: item.rd || "",
    label: item.label || "",
    familyAllowed: item.familyallowed || "",
  }));
}

interface DetailField {
  label: string;
  key: keyof ExtendedBgpTables;
}

/**
 * The two columns of the expanded route row. Next Hop, Origin, RD, Label and
 * Family allowed are what the old dashboard showed there and this one did not
 * (CTX-1336); bgpsumary always returned them.
 */
export const BGP_DETAIL_COLUMNS: [DetailField[], DetailField[]] = [
  [
    { label: "Prefix", key: "prefix" },
    { label: "Direction", key: "direction" },
    { label: "Neighbour", key: "neighbor" },
    { label: "Next Hop", key: "nextHop" },
    { label: "Local IP", key: "localIp" },
    { label: "Local AS", key: "localAs" },
    { label: "Peer AS", key: "peerAs" },
    { label: "Origin", key: "origin" },
    { label: "Route Table", key: "route_table" },
  ],
  [
    { label: "Router ID", key: "routerId" },
    { label: "AS Path", key: "asPath" },
    { label: "Family", key: "family" },
    { label: "Family allowed", key: "familyAllowed" },
    { label: "Local Pref", key: "localPref" },
    { label: "Community", key: "community" },
    { label: "Ext. Community", key: "extendedCommunity" },
    { label: "RD", key: "rd" },
    { label: "Label", key: "label" },
    { label: "Subnet", key: "subnet" },
  ],
];

export function BgpTablesPage() {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const { tenantId } = useAppSelector((state) => state.selection);
  const previousTenantIdRef = useRef<number | undefined>(undefined);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isInitialMountRef = useRef(true);

  const [isFetching, setIsFetching] = useState(false);
  // Usaremos ExtendedBgpTables (7 col + extra)
  const [tablesData, setTablesData] = useState<ExtendedBgpTables[]>([]);

  // Filtro local con GenericSearchbar
  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);
  const bgpFields = useMemo(() => makeBgpFields(), []);

  // Generate suggested keys from field labels
  const suggestedKeys = useMemo(() => {
    return bgpFields.map((f) => f.label);
  }, [bgpFields]);

  // 1) CTX-R
  const [fullCTXRlist, setFullCTXRlist] = useState<CtxrItem[]>([]);
  const [firstOptions, setFirstOptions] = useState<string[]>([]);
  const [selectedFirst, setSelectedFirst] = useState("");

  // 2) Neighbors
  const [secondOptions, setSecondOptions] = useState<string[]>([]);
  const [selectedSecond, setSelectedSecond] = useState("All");

  // 3) Direction
  const directionOptions = ["All", "In", "Out"];
  const [selectedDirection, setSelectedDirection] = useState("All");

  // Helper function to find CTX-R object by display name
  const findCtxrByDisplayName = useCallback(
    (displayName: string): CtxrItem | undefined => {
      return fullCTXRlist.find(
        (obj) => `${obj.name} (${obj.description})` === displayName
      );
    },
    [fullCTXRlist]
  );

  // Helper function to clear/reset dropdown data
  const clearDropdownData = useCallback(() => {
    setTablesData([]);
    setSecondOptions([]);
    setSelectedSecond("All");
    setSelectedDirection("All");
  }, []);

  // Helper function to check if request should be aborted
  const isRequestAborted = useCallback(
    (err: any, abortController: AbortController): boolean => {
      return (
        axios.isCancel(err) ||
        err.name === "AbortError" ||
        abortController.signal.aborted
      );
    },
    []
  );

  // Centralized function to fetch BGP data
  const fetchBgpData = useCallback(
    async (
      ctxrId: string,
      neighbor: string,
      direction: string,
      skipLoadingState = false
    ) => {
      // Cancel previous request if exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        if (!skipLoadingState) {
          setIsFetching(true);
        }

        const token = await getAccessTokenSilently();
        const neighborParam = neighbor === "All" ? "" : neighbor;
        const typeParam = direction.toLowerCase();

        const resp = await postBgpSummary(
          token,
          ctxrId,
          tenantId,
          {
            neighbor: neighborParam,
            type: typeParam,
          },
          abortController.signal
        );

        // Check if request was aborted
        if (abortController.signal.aborted) {
          return;
        }

        const shaped = mapBgpSummaryToRows(resp.data as BgpSummaryItem[]);
        setTablesData(shaped);
      } catch (err: any) {
        // Don't show error if request was aborted
        if (isRequestAborted(err, abortController)) {
          return;
        }
        console.error("Error fetching BGP data =>", err);
        // Only show alert for manual refresh, not for auto-refresh
        if (!skipLoadingState) {
          alert(
            "Error fetching BGP data. Please check the console for details."
          );
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsFetching(false);
        }
      }
    },
    [tenantId, getAccessTokenSilently, isRequestAborted]
  );

  // Initial load when component mounts or auth/tenant changes
  useEffect(() => {
    if (isLoading || !isAuthenticated || !tenantId) return;

    // Check if tenantId has actually changed
    const hasTenantChanged =
      previousTenantIdRef.current !== undefined &&
      previousTenantIdRef.current !== tenantId;

    if (hasTenantChanged) {
      // Clear previous data when tenant changes
      clearDropdownData();
      // Reset initial mount flag so auto-refresh works after tenant change
      isInitialMountRef.current = true;
    }

    (async () => {
      setIsFetching(true);
      try {
        const token = await getAccessTokenSilently();
        const resp = await getCTXRlistGlobal(-1, tenantId, token);
        const dataArray: CtxrItem[] = resp.data || [];

        setFullCTXRlist(dataArray);
        const mapped = dataArray.map(
          (item) => `${item.name} (${item.description})`
        );
        setFirstOptions(mapped);

        if (mapped.length > 0) {
          setSelectedFirst(mapped[0]);
          // Update ref after setting the first option
          previousTenantIdRef.current = tenantId;
        }
      } catch (err) {
        console.error("Error =>", err);
      } finally {
        setIsFetching(false);
      }
    })();
  }, [
    isLoading,
    isAuthenticated,
    tenantId,
    getAccessTokenSilently,
    clearDropdownData,
  ]);

  // Auto-refresh when tenantId changes and we have a selected CTX-R
  useEffect(() => {
    if (
      isLoading ||
      !isAuthenticated ||
      !tenantId ||
      !selectedFirst ||
      !fullCTXRlist.length
    )
      return;
    // Only auto-refresh if tenant actually changed (not on initial mount)
    const hasTenantChanged =
      previousTenantIdRef.current !== undefined &&
      previousTenantIdRef.current !== tenantId;
    if (!hasTenantChanged) return;
    // Auto-execute refresh when tenant changes
    const foundObj = findCtxrByDisplayName(selectedFirst);
    if (foundObj) {
      fetchBgpData(foundObj.id, selectedSecond, selectedDirection, false);
    }
  }, [
    tenantId,
    selectedFirst,
    selectedSecond,
    selectedDirection,
    isLoading,
    isAuthenticated,
    fullCTXRlist.length,
    findCtxrByDisplayName,
    fetchBgpData,
  ]);

  // Auto-refresh when dropdown selections change (with debouncing)
  useEffect(() => {
    if (
      isLoading ||
      !isAuthenticated ||
      !tenantId ||
      !selectedFirst ||
      !fullCTXRlist.length
    )
      return;

    // Skip auto-refresh on initial mount (first load is handled by tenantId change effect)
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }

    // Clear previous debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Find the selected CTX-R object
    const foundObj = findCtxrByDisplayName(selectedFirst);
    if (!foundObj) {
      return;
    }

    // Debounce the request to avoid too many API calls
    debounceTimerRef.current = setTimeout(() => {
      fetchBgpData(foundObj.id, selectedSecond, selectedDirection, false);
    }, 400); // 400ms debounce delay

    // Cleanup function
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [
    selectedFirst,
    selectedSecond,
    selectedDirection,
    fullCTXRlist.length,
    tenantId,
    isLoading,
    isAuthenticated,
    fetchBgpData,
    findCtxrByDisplayName,
  ]);

  // Al cambiar CTX-R => neighbors
  const handleSelectFirst = useCallback(
    async (val: string) => {
      setSelectedFirst(val);
      setSelectedSecond("All");
      setSecondOptions([]);
      setSelectedDirection("All");

      try {
        setIsFetching(true);
        const foundObj = findCtxrByDisplayName(val);
        if (!foundObj) {
          setSecondOptions(["All"]);
          return;
        }

        const token = await getAccessTokenSilently();
        const resp = await getNeighborsListByCtxrId(
          token,
          foundObj.id,
          tenantId
        );
        const rawNeighbors = resp.data as Array<{ neighbor: string }>;

        if (!rawNeighbors.length) {
          setSecondOptions(["All"]);
        } else {
          const neighborsList = ["All", ...rawNeighbors.map((n) => n.neighbor)];
          setSecondOptions(neighborsList);
        }
      } catch (err) {
        console.error("Error loading neighbors =>", err);
      } finally {
        setIsFetching(false);
      }
    },
    [fullCTXRlist, tenantId, getAccessTokenSilently, findCtxrByDisplayName]
  );

  const handleSelectSecond = useCallback(
    (val: string) => setSelectedSecond(val),
    []
  );
  const handleSelectDirection = useCallback(
    (val: string) => setSelectedDirection(val),
    []
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      // Abort any pending requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Build maps for filter normalization (similar to MainPort and LogsPage)
  const fieldById = useMemo(() => {
    const m = new Map<string, FilterField<ExtendedBgpTables>>();
    for (const f of bgpFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      m.set(id, f);
    }
    return m;
  }, [bgpFields]);
  const keyMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of bgpFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      const label = f.label.toLowerCase();
      m.set(id, id);
      m.set(label, id);
    }
    return m;
  }, [bgpFields]);

  // Normalize filters (similar to MainPort and LogsPage)
  const normalizedFilters = useMemo(() => {
    return activeFilters.map((f) => {
      // If searchbar provided an op (live typing), keep it
      const token = f.op
        ? { ...f }
        : parseToken(f.key ? `${f.key}=${f.value}` : f.value);

      // Map key to canonical id
      if (token.key) {
        const k = token.key.trim().toLowerCase();
        token.key = keyMap.get(k) ?? labelId(token.key);
      }

      // If this is a STRING field and user didn't explicitly ask for exact,
      const field = token.key ? fieldById.get(token.key) : undefined;
      if (field?.type === "string") {
        const quoted = token.value.match(/^"(.*)"$/);
        if (quoted) {
          token.value = quoted[1]; // strip quotes
          token.op = "=";
        } else {
          token.op = ":"; // default to contains
        }
      }
      return token;
    });
  }, [activeFilters, keyMap, fieldById]);
  // Build predicate using advancedFilters system
  const predicate = useMemo(
    () => buildPredicate<ExtendedBgpTables>(normalizedFilters, bgpFields),
    [normalizedFilters, bgpFields]
  );

  // Apply predicate to filter data
  const filteredData = useMemo(
    () => tablesData.filter(predicate),
    [tablesData, predicate]
  );

    // Helper component for detail rows
    const DetailRow: React.FC<{ label: string; value: string | undefined }> = ({
      label,
      value,
    }) => (
      <div className="py-1 flex justify-between border-b">
        <span className="text-semantic-text-secondary font-semibold">{label}</span>
        <span className="text-semantic-text-secondary">{value || "—"}</span>
      </div>
    );

  // Fila expandida en 2 columnas (estilo Figma)
  const renderExpanded = (row: ExtendedBgpTables) => {
    const [firstColumnFields, secondColumnFields] = BGP_DETAIL_COLUMNS;
    return (
      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Primera columna */}
        <GenericCard
          isCollapsible={false}
          isLoading={isLoading}
          bodyContent={
            <>
              {firstColumnFields.map((field) => (
                <DetailRow
                  key={field.key}
                  label={field.label}
                  value={row[field.key] as string | undefined}
                />
              ))}
            </>
          }
        />
        {/* Segunda columna */}
        <GenericCard
          isCollapsible={false}
          isLoading={isLoading}
          bodyContent={
            <>
              {secondColumnFields.map((field) => (
                <DetailRow
                  key={field.key}
                  label={field.label}
                  value={row[field.key] as string | undefined}
                />
              ))}
            </>
          }
        />
        </div>
    );
  };

  // Aquí definimos el *botón personalizado* para expandir/cerrar.
  const renderExpandToggle = (
    isExpanded: boolean,
    onToggle: () => void,
    row: ExtendedBgpTables
  ) => {
    void row;
    return (
      <button
        onClick={() => {
          console.log("[renderExpandToggle -> onClick]");
          onToggle();
        }}
        className="
                border border-semantic-border-secondary
                rounded
                bg-semantic-bg-primary
                text-semantic-fg-secondary
                hover:bg-semantic-state-active
                w-10 h-10
                flex items-center justify-center
              "
      >
        {isExpanded ? (
          <IconClosed className="w-4 h-4" />
        ) : (
          <IconStar className="w-4 h-4" />
        )}
      </button>
    );
  };

  return (
    <div className="relative min-h-[500px]">
      {/* Encabezado con combos + Refresh */}
      <div className="mb-4 p-4 bg-semantic-bg-primary border border-semantic-border-secondary rounded-sm">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* CTX-R */}
            <GenericDropdown
              items={firstOptions}
              value={selectedFirst}
              onSelect={handleSelectFirst}
              minWidthClass="min-w-full lg:min-w-[260px]"
              widthClass="w-full lg:w-[260px]"
              defaultLabel="-- Select instance --"
              label="CTX-R"
              dropdownMaxHeightClass="max-h-48"
            />
            <GenericDropdown
              items={secondOptions}
              value={selectedSecond}
              onSelect={handleSelectSecond}
              minWidthClass="min-w-full lg:min-w-[260px]"
              widthClass="w-full lg:w-[260px]"
              defaultLabel="All"
              label="Neighbor"
              dropdownMaxHeightClass="max-h-48"
            />
            <GenericDropdown
              items={directionOptions}
              value={selectedDirection}
              onSelect={handleSelectDirection}
              minWidthClass="min-w-full lg:min-w-[260px]"
              widthClass="w-full lg:w-[260px]"
              defaultLabel="All"
              label="Direction"
              dropdownMaxHeightClass="max-h-48"
            />
          </div>
      </div>

      <div className="pb-6">
        <GenericCard
          isCollapsible={false}
          isLoading={isLoading}
          bodyContent={
            <>
              <div className="w-full max-w-full sm:max-w-[500px] my-5">
                <GenericSearchbar
                  placeholder="Find by attribute or value (e.g., Prefix: 192.168.1.0/24)"
                  suggestedKeys={suggestedKeys}
                  onFiltersChange={setActiveFilters}
                  liveFiltering={true}
                  debounceMs={200}
                />
              </div>

              {/* Tabla => 7 columnas, expandible => renderExpanded */}
              {isFetching ? (
                  <div className="p-4">
                      <SkeletonTableRows
                          rows={5}
                          cols={5}
                          colWidths={["200px"]}
                          rowHeight={15}
                          rowGapClassName="space-y-3"
                      />
                  </div>
              ) : (
                <GenericTable<ExtendedBgpTables>
                  columns={tablesColumns}
                  data={filteredData}
                  initialRowsPerPage={10}
                  rowsPerPageOptions={[5, 10, 20, 50, 100]}
                  width="100%"
                  tableClassName="bg-semantic-bg-primary"
                  // Subfila de 2 col
                  expandable
                  renderExpanded={renderExpanded}
                  // Aquí pasamos nuestro botón custom:
                  renderExpandToggle={renderExpandToggle}
                />
              )}
            </>
          }
        />
      </div>
    </div>
  );
}

export default BgpTablesPage;
